import { StreamManager } from './StreamManager';
import { DebugStream } from '../internal/DebugStream';
import { StreamDataCapture } from './StreamDataCapture';
import { defaultConfig } from '../../../../../utils/config';
import { streamLog } from './streamLogger';
import { setCurrentTurnId, resetTurnState, setPendingTextBoundary } from './streamStore';
import type {
  StartArgs,
  StreamEvent,
} from './Types';

export type Transport = (
  args: StartArgs,
  onEvent: (e: StreamEvent) => void
) => { cancel: () => void; done: Promise<void>; capture?: any };

export function sseTransport(url: string): Transport {
  return (args: StartArgs, onEvent: (e: StreamEvent) => void) => {
    const manager = new StreamManager();
    const pendingIdsByName = new Map<string, string[]>();
    const countersByName = new Map<string, number>();
    const assignIdForName = (name: string) => {
      const n = (countersByName.get(name) || 0) + 1;
      countersByName.set(name, n);
      const id = `tool_${name}_${n}`;
      const q = pendingIdsByName.get(name) || [];
      q.push(id);
      pendingIdsByName.set(name, q);
      return id;
    };
    const takePendingIdForName = (name: string) => {
      const q = pendingIdsByName.get(name) || [];
      const id = q.shift();
      pendingIdsByName.set(name, q);
      return id;
    };
    let resolveDone: () => void;
    let rejectDone: (e: any) => void;
    const done = new Promise<void>((resolve, reject) => {
      resolveDone = resolve;
      rejectDone = reject;
    });

    // Create capture instance at transport level
    let capture: StreamDataCapture | null = null;
    if (defaultConfig.CAPTURE_STREAM_DATA) {
      try {
        const gameId = args.request.gameId || 'unknown';
        capture = new StreamDataCapture(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify({
            prompt: args.request.prompt,
            historyEntryId: args.request.historyEntryId,
            gameId: args.request.gameId,
            isInitialCreate: args.request.isInitialCreate,
          }),
        }, gameId);
      } catch (error) {
        try { streamLog.error('🚨 Failed to initialize stream capture in transport:', error); } catch (_) {}
      }
    }

    const normalizedUrl = url.replace(/\/$/, '');
    const body = {
      prompt: args.request.prompt,
      historyEntryId: args.request.historyEntryId,
      gameId: args.request.gameId,
      isInitialCreate: args.request.isInitialCreate,
    };

    // Notify connecting phase
    onEvent({ type: 'phase', to: 'Connecting' });

    manager
      .connectViaFetch(
        normalizedUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            ...args.headers,
          },
          body: JSON.stringify(body),
        },
        {
          onMetadata: (data: any) => {
            if (data?.type === 'phase_transition' && data?.to) {
              onEvent({ type: 'phase', to: data.to });
            }
            try {
              const payload: any = data;
              if (payload && payload.type === 'turn_start') {
                const turnId = (payload as any).turn_id as string | undefined;
                if (turnId) {
                  setCurrentTurnId(turnId);
                  resetTurnState();
                  setPendingTextBoundary(true);
                }
              } else if (payload && payload.type === 'turn_end') {
                setPendingTextBoundary(true);
              }
            } catch (_) {}
            onEvent({ type: 'metadata', payload: data });
          },
          onToolCall: (data: any) => {
            const name = data.toolName || data.type || 'Tool';
            const id = data.toolId || data.id || assignIdForName(name);
            const args = (data && typeof data === 'object') ? (data.args || {}) : {};
            const meta = (data && typeof data === 'object') ? (data.meta || {}) : {};
            const params = { args };
            onEvent({ type: 'tool_call', id, name, params, meta, phase: (data && typeof data === 'object' && (data as any).phase) || undefined });
          },
          onToolResult: (data: any) => {
            const name = data.toolName || data.type || data.name || 'Tool';
            const id = data.toolId || data.id || takePendingIdForName(name) || `tool_${name}_result`;
            const resultBase = (data && typeof data === 'object') ? (data.result || {}) : {};
            const status = (data && typeof data === 'object' && typeof (data as any).status === 'string') ? (data as any).status : undefined;
            const error = (data && typeof data === 'object') ? ((data as any).error || undefined) : undefined;
            const result = status ? { ...resultBase, __status: status, __error: error } : resultBase;
            const meta = (data && typeof data === 'object') ? (data.meta || {}) : {};
            onEvent({ type: 'tool_result', id, name, result, meta, phase: (data && typeof data === 'object' && (data as any).phase) || undefined });
          },
          onAIContent: (data: any) => {
            const payload = (data && typeof data === 'object' && 'data' in (data as any)) ? (data as any).data : data;
            const chunk: string = typeof (payload as any)?.content === 'string'
              ? (payload as any).content
              : (typeof (payload as any)?.deltaContent === 'string' ? (payload as any).deltaContent : '');
            if (chunk) {
              const meta = (payload && typeof payload === 'object') ? ((payload as any).meta || {}) : {};
              onEvent({ type: 'text_delta', delta: chunk, meta, phase: (data && typeof data === 'object' && (data as any).phase) || undefined });
            }
          },
          onComplete: (data: any) => {
            onEvent({ type: 'complete', content: data?.content });
          },
          onDone: () => {
            resolveDone();
          },
          onError: (error: Error) => {
            onEvent({ type: 'error', error });
            rejectDone(error);
          }
        },
        capture
      )
      .catch((e: any) => {
        onEvent({ type: 'error', error: e instanceof Error ? e : new Error(String(e)) });
        rejectDone(e);
      });

    return {
      cancel: () => {
        manager.disconnect();
      },
      done,
      capture,
    };
  };
}

// SSE transport that posts a custom JSON body (e.g., ART payload)
export function sseTransportWithBody(url: string, bodyPayload: Record<string, unknown>): Transport {
  return (args: StartArgs, onEvent: (e: StreamEvent) => void) => {
    const manager = new StreamManager();
    const pendingIdsByName = new Map<string, string[]>();
    const countersByName = new Map<string, number>();
    const assignIdForName = (name: string) => {
      const n = (countersByName.get(name) || 0) + 1;
      countersByName.set(name, n);
      const id = `tool_${name}_${n}`;
      const q = pendingIdsByName.get(name) || [];
      q.push(id);
      pendingIdsByName.set(name, q);
      return id;
    };
    const takePendingIdForName = (name: string) => {
      const q = pendingIdsByName.get(name) || [];
      const id = q.shift();
      pendingIdsByName.set(name, q);
      return id;
    };
    let resolveDone: () => void;
    let rejectDone: (e: any) => void;
    const done = new Promise<void>((resolve, reject) => {
      resolveDone = resolve;
      rejectDone = reject;
    });

    const normalizedUrl = url.replace(/\/$/, '');

    // Notify connecting phase
    onEvent({ type: 'phase', to: 'Connecting' });

    manager
      .connectViaFetch(
        normalizedUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            ...args.headers,
          },
          body: JSON.stringify(bodyPayload),
        },
        {
          onMetadata: (data: any) => {
            if (data?.type === 'phase_transition' && data?.to) {
              onEvent({ type: 'phase', to: data.to });
            }
            try {
              const payload: any = data;
              if (payload && payload.type === 'turn_start') {
                const turnId = (payload as any).turn_id as string | undefined;
                if (turnId) {
                  setCurrentTurnId(turnId);
                  resetTurnState();
                  setPendingTextBoundary(true);
                }
              } else if (payload && payload.type === 'turn_end') {
                setPendingTextBoundary(true);
              }
            } catch (_) {}
            onEvent({ type: 'metadata', payload: data });
          },
          onToolCall: (data: any) => {
            const name = data.toolName || data.type || 'Tool';
            const id = data.toolId || data.id || assignIdForName(name);
            const params = data.arguments || data.params || data.args || data;
            const meta = (data && typeof data === 'object') ? (data.meta || {}) : {};
            onEvent({ type: 'tool_call', id, name, params, meta });
          },
          onToolResult: (data: any) => {
            const name = data.toolName || data.type || data.name || 'Tool';
            const id = data.toolId || data.id || takePendingIdForName(name) || `tool_${name}_result`;
            const base = data.result || data.params || data;
            const status = (data && typeof data === 'object' && typeof (data as any).status === 'string') ? (data as any).status : undefined;
            const error = (data && typeof data === 'object') ? ((data as any).error || undefined) : undefined;
            const result = status ? { ...base, __status: status, __error: error } : base;
            const meta = (data && typeof data === 'object') ? (data.meta || {}) : {};
            onEvent({ type: 'tool_result', id, name, result, meta });
          },
          onAIContent: (data: any) => {
            const chunk: string = typeof data?.content === 'string'
              ? data.content
              : (typeof data?.deltaContent === 'string' ? data.deltaContent : '');
            if (chunk) {
              const meta = (data && typeof data === 'object') ? ((data as any).meta || {}) : {};
              onEvent({ type: 'text_delta', delta: chunk, meta });
            }
          },
          onComplete: (data: any) => {
            onEvent({ type: 'complete', content: data?.content });
          },
          onDone: () => {
            resolveDone();
          },
          onError: (error: Error) => {
            onEvent({ type: 'error', error });
            rejectDone(error);
          }
        }
      )
      .catch((e: any) => {
        onEvent({ type: 'error', error: e instanceof Error ? e : new Error(String(e)) });
        rejectDone(e);
      });

    return {
      cancel: () => {
        manager.disconnect();
      },
      done,
    };
  };
}

export function debugTransport(debug: DebugStream): Transport {
  return (_args: StartArgs, onEvent: (e: StreamEvent) => void) => {
    let resolveDone: () => void;
    let rejectDone: (e: any) => void;
    const done = new Promise<void>((resolve, reject) => {
      resolveDone = resolve;
      rejectDone = reject;
    });

    onEvent({ type: 'phase', to: 'Debug Mode' });

    try {
      debug.simulate({
        onMetadata: (data: any) => {
          if (data?.type === 'phase_transition' && data?.to) {
            onEvent({ type: 'phase', to: data.to });
          }
          try {
            const payload: any = data;
            if (payload && payload.type === 'turn_start') {
              const turnId = (payload as any).turn_id as string | undefined;
              if (turnId) {
                setCurrentTurnId(turnId);
                resetTurnState();
                setPendingTextBoundary(true);
              }
            } else if (payload && payload.type === 'turn_end') {
              setPendingTextBoundary(true);
            }
          } catch (_) {}
          onEvent({ type: 'metadata', payload: data });
        },
        onToolCall: (data: any) => {
          const id = data.toolId || data.id;
          const name = data.toolName || data.type || 'Tool';
          const args = (data && typeof data === 'object') ? (data.args || {}) : {};
          const meta = (data && typeof data === 'object') ? (data.meta || {}) : {};
          const params = { args };
          onEvent({ type: 'tool_call', id: id || `tool_${Date.now()}`, name, params, meta, phase: (data && typeof data === 'object' && (data as any).phase) || undefined });
        },
        onToolResult: (data: any) => {
          const id = data.toolId || data.id || `tool_${Date.now()}`;
          const name = data.toolName || data.type || data.name; // normalize for UI
          const base = (data && typeof data === 'object') ? (data.result || {}) : {};
          const status = (data && typeof data === 'object' && typeof (data as any).status === 'string') ? (data as any).status : undefined;
          const error = (data && typeof data === 'object') ? ((data as any).error || undefined) : undefined;
          const result = status ? { ...base, __status: status, __error: error } : base;
          const meta = (data && typeof data === 'object') ? (data.meta || {}) : {};
          onEvent({ type: 'tool_result', id, name, result, meta, phase: (data && typeof data === 'object' && (data as any).phase) || undefined });
        },
        onAIContent: (data: any) => {
          const chunk: string = typeof data?.content === 'string'
            ? data.content
            : (typeof data?.deltaContent === 'string' ? data.deltaContent : '');
          if (chunk) {
            const meta = (data && typeof data === 'object') ? ((data as any).meta || {}) : {};
            onEvent({ type: 'text_delta', delta: chunk, meta, phase: (data && typeof data === 'object' && (data as any).phase) || undefined });
          }
        },
        onComplete: (data: any) => {
          onEvent({ type: 'complete', content: data?.content });
        },
        onDone: () => {
          resolveDone();
        },
        onError: (error: Error) => {
          onEvent({ type: 'error', error });
          rejectDone(error);
        }
      });
    } catch (e) {
      onEvent({ type: 'error', error: e as Error });
      rejectDone(e);
    }

    return {
      cancel: () => debug.stop(),
      done,
      capture: undefined,
    };
  };
}

export function nonStreamingTransport(url: string): Transport {
  return (args: StartArgs, onEvent: (e: StreamEvent) => void) => {
    let resolveDone: () => void;
    let rejectDone: (e: any) => void;
    const done = new Promise<void>((resolve, reject) => {
      resolveDone = resolve;
      rejectDone = reject;
    });

    (async () => {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...args.headers,
          },
          body: JSON.stringify({
            prompt: args.request.prompt,
            historyEntryId: args.request.historyEntryId,
            gameId: args.request.gameId,
            isInitialCreate: args.request.isInitialCreate,
            requestStreaming: false,
          }),
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          const error = new Error(`Failed to generate game: ${response.status} ${text}`);
          onEvent({ type: 'error', error });
          rejectDone(error);
          return;
        }

        const content = await response.text();
        if (content) onEvent({ type: 'text_delta', delta: content });
        onEvent({ type: 'complete', content });
        resolveDone();
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        onEvent({ type: 'error', error: err });
        rejectDone(err);
      }
    })();

    return {
      cancel: () => {},
      done,
      capture: undefined,
    };
  };
}


